const URL_PARAM = 'experience';

/**
 * A custom element that filters the visit experience cards by experience type.
 *
 * The section only renders tabs for the types its cards actually use, so a
 * filter can never resolve to zero visible cards.
 */
class VisitExperiencesFilter extends HTMLElement {
  /** @type {HTMLElement[]} */
  #tabs = [];

  /** @type {HTMLElement[]} */
  #cards = [];

  /** @type {HTMLElement | null} */
  #count = null;

  connectedCallback() {
    this.#tabs = Array.from(this.querySelectorAll('[data-experience-filter]'));
    if (this.#tabs.length === 0) return;

    this.#cards = Array.from(this.querySelectorAll('.experience-card'));
    this.#count = this.querySelector('[data-experience-count]');

    this.addEventListener('click', this.#handleClick);
    this.#apply(this.#filterFromUrl(), false);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
  }

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const tab = event.target.closest('[data-experience-filter]');
    if (!tab) return;

    this.#apply(tab.dataset.experienceFilter ?? '', true);
  };

  /**
   * Reads the active filter from the URL, ignoring values without a matching tab.
   *
   * @returns {string} The experience type, or an empty string for all.
   */
  #filterFromUrl() {
    const requested = new URLSearchParams(window.location.search).get(URL_PARAM);
    if (!requested) return '';

    return this.#tabs.some((tab) => tab.dataset.experienceFilter === requested) ? requested : '';
  }

  /**
   * @param {string} filter - The experience type to show, or an empty string for all.
   * @param {boolean} updateUrl - Whether to reflect the filter in the URL.
   */
  #apply(filter, updateUrl) {
    for (const tab of this.#tabs) {
      tab.setAttribute('aria-pressed', String((tab.dataset.experienceFilter ?? '') === filter));
    }

    let visible = 0;

    for (const card of this.#cards) {
      const matches = filter === '' || card.dataset.experienceType === filter;
      card.hidden = !matches;
      if (!matches) continue;

      // Cards alternate backgrounds, so re-stripe whatever stays visible.
      card.classList.toggle('bg-snow-white', visible % 2 === 0);
      card.classList.toggle('bg-oak-ash', visible % 2 === 1);
      visible += 1;
    }

    if (this.#count) {
      this.#count.textContent = `${visible} ${visible === 1 ? 'experience' : 'experiences'}`;
    }

    if (updateUrl) this.#updateUrl(filter);
  }

  /** @param {string} filter */
  #updateUrl(filter) {
    const url = new URL(window.location.href);

    if (filter) {
      url.searchParams.set(URL_PARAM, filter);
    } else {
      url.searchParams.delete(URL_PARAM);
    }

    window.history.replaceState({}, '', url);
  }
}

if (!customElements.get('visit-experiences-filter')) {
  customElements.define('visit-experiences-filter', VisitExperiencesFilter);
}
